/** The page shown when the URL names nothing the app can open.
 *
 *  Deliberately NOT routed through ErrorBoundary. A dead link is not a crash:
 *  there is no stack to capture, no error id worth quoting, and nothing for
 *  support to fix — filing it as a bug report would bury real crashes in noise
 *  and tell the user their mistyped URL is being investigated. So this borrows
 *  only the COPY from the error-kind table (`getErrorKindConfig('notFound')`),
 *  which keeps the wording in one place, and none of the machinery.
 *
 *  Why it exists at all: an unknown path used to be swallowed silently. The app
 *  rendered whatever view was left in localStorage from last session, so a
 *  revoked or mistyped link looked like it had worked and the user had no way
 *  to tell they were somewhere they had not asked for.
 */

import { getErrorKindConfig } from './ErrorBoundary';

interface Props {
  /** The path that matched nothing. Shown back to the user because on a
   *  mistyped or truncated link it is usually the whole explanation. */
  pathname: string;
}

export function RouteNotFound({ pathname }: Props) {
  const { title, message } = getErrorKindConfig('notFound');

  // Back is offered only when there is somewhere to go back TO. Arriving cold
  // on a bad link is the common case, and a Back button that does nothing is
  // worse than no button.
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  return (
    <div
      role="main"
      className="font-apple"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: 24,
        textAlign: 'center',
        background: 'var(--background)',
        color: 'var(--foreground)',
      }}
    >
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{title}</h1>
      <p style={{ fontSize: 14, color: 'var(--muted-foreground)', margin: 0, maxWidth: 420 }}>
        {message}
      </p>
      <code
        style={{
          fontSize: 12,
          color: 'var(--muted-foreground)',
          background: 'var(--muted)',
          padding: '4px 10px',
          borderRadius: 6,
          marginTop: 2,
          maxWidth: '90vw',
          overflowWrap: 'anywhere',
        }}
      >
        {pathname}
      </code>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        {canGoBack && (
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--foreground)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Go back
          </button>
        )}
        <button
          onClick={() => { window.location.href = '/'; }}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid transparent',
            background: 'var(--brand)',
            color: 'white',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Go to Quantis
        </button>
      </div>
    </div>
  );
}
